import moment from 'moment';
import React from 'react';
import theme from '../../../../../../assets/styles/theme/theme';
import { IBilledOrderData } from '../../../../../../store/ducks/long-steel-orders/types';
import { formatCurrency } from '../../../../../../utils/formatNumbers';
import {
  excelNumberCell,
  IExcelNumberCell,
  isExcelNumberCell
} from '../../../../../../utils/createXMLTable';

interface IOpenOrderTableContentToExcelProps {
  orders: IBilledOrderData[];
}

interface ITitleTd {
  title: string;
  width: number;
  backgroundColor: string;
}

interface ITableTr {
  tdsContent: Array<React.Key | IExcelNumberCell>;
}

const OpenOrderTableContentToExcel: React.FC<
  IOpenOrderTableContentToExcelProps
> = ({ orders }) => {
  const headerBackgroundColorBlue = theme.colors.blue2;
  const docTypeHeaderColor = theme.colors.green2;
  const notesHeaderColor = theme.colors.gray5;
  const orderDetailHeaderColor = theme.colors.yellow;
  const billedDetailHeaderColor = theme.colors.beige;
  const evenRowColor = theme.colors.white3;

  const getTitleTds = (): ITitleTd[] => {
    const titleTds: ITitleTd[] = [];

    const addTitleTd = (
      title: string,
      width: number,
      backgroundColor: string
    ) => {
      titleTds.push({ title, width, backgroundColor });
    };

    addTitleTd('Tipo de documento', 115, docTypeHeaderColor);

    addTitleTd('Nota Fiscal', 115, notesHeaderColor);
    addTitleTd('Chave de acesso NF', 115, notesHeaderColor);
    addTitleTd('Nota de remessa', 115, notesHeaderColor);

    addTitleTd('Emissão da nota', 115, headerBackgroundColorBlue);
    addTitleTd('Data de vencimento', 115, headerBackgroundColorBlue);
    addTitleTd('Material cliente', 80, headerBackgroundColorBlue);
    addTitleTd('Material Gerdau', 100, headerBackgroundColorBlue);
    addTitleTd('Descrição do produto', 255, headerBackgroundColorBlue);
    addTitleTd('Qtd. Faturada do item', 90, headerBackgroundColorBlue);
    addTitleTd('Un.', 60, headerBackgroundColorBlue);
    addTitleTd('Valor unitário', 90, headerBackgroundColorBlue);
    addTitleTd(
      'Valor total do item s/ imposto',
      100,
      headerBackgroundColorBlue
    );
    addTitleTd(
      'Valor total do item com imposto',
      130,
      headerBackgroundColorBlue
    );

    addTitleTd('N° da OV Gerdau', 65, orderDetailHeaderColor);
    addTitleTd('N° item', 65, orderDetailHeaderColor);
    addTitleTd('Nº do pedido', 65, orderDetailHeaderColor);
    addTitleTd('Criação do pedido', 100, orderDetailHeaderColor);
    addTitleTd('Condição de pagamento', 183, orderDetailHeaderColor);
    addTitleTd('Recebedor', 230, orderDetailHeaderColor);

    addTitleTd(
      'Valor total da fatura sem impostos',
      95,
      billedDetailHeaderColor
    );
    addTitleTd(
      'Valor total da Fatura com impostos',
      95,
      billedDetailHeaderColor
    );
    addTitleTd('Volume total da nota (ton)', 100, billedDetailHeaderColor);
    addTitleTd('Nº Fatura', 100, billedDetailHeaderColor);
    addTitleTd('Corrida', 100, billedDetailHeaderColor);

    return titleTds;
  };
  const allTitleTds = getTitleTds();

  const getDueDates = (dueDates: string[]) => {
    if (
      dueDates.some((dueDate) => dueDate === 'Compensado' || dueDate === '')
    ) {
      return 'Pago';
    }

    const dates: string[] = [];
    dueDates.map((dueDate) =>
      dates.push(moment(dueDate, 'YYYY-MM-DD').format('DD/MM/YYYY'))
    );
    return dates.join(', ');
  };

  const getCurrencyValue = (value: string): string => {
    if (value === 'Indisponível') {
      return ' - ';
    } else {
      return formatCurrency(value);
    }
  };

  const getTableLines = (): ITableTr[] => {
    const trs: ITableTr[] = [];

    orders.forEach((item) => {
      const tdsContent: Array<React.Key | IExcelNumberCell> = [];

      tdsContent.push('Fatura');

      tdsContent.push(item.invoiceNumber);
      tdsContent.push(`${item.nfeAccessKey}'`);
      tdsContent.push(item.invoiceZvoi || ' - ');

      tdsContent.push(moment(item.itemBillingDate).format('DD/MM/YYYY'));
      tdsContent.push(getDueDates(item.dueDate));
      tdsContent.push(item.billingHistory.customerSKU || ' - ');
      tdsContent.push(item.billingHistory.sku);
      tdsContent.push(item.billingHistory.materialDescription);
      tdsContent.push(
        excelNumberCell(item.quantities.billingQuantity) ?? ' - '
      );
      tdsContent.push(item.quantities.billingMeasureUnit);
      tdsContent.push(getCurrencyValue(item.conditions.unitPriceWithTaxes));
      tdsContent.push(
        getCurrencyValue(item.conditions.invoiceItemValueWithoutTaxes)
      );
      tdsContent.push(
        getCurrencyValue(item.conditions.invoiceItemValueWithTaxes)
      );

      tdsContent.push(item.billingHistory.salesOrder);
      tdsContent.push(item.billingHistory.salesOrderItem);
      tdsContent.push(`${item.billingHistory.customerOrder}'`);
      tdsContent.push(
        moment(item.billingHistory.itemCreationDate).format('DD/MM/YY')
      );
      tdsContent.push(item.conditions.paymentTermsDescription);
      tdsContent.push(
        `${item.shipTo.name} - ${item.shipTo.address}, ${item.shipTo.city} - ${item.shipTo.state}`
      );

      tdsContent.push(
        getCurrencyValue(item.conditions.invoiceTotalValueWithoutTaxes)
      );
      tdsContent.push(
        getCurrencyValue(item.conditions.invoiceTotalValueWithTaxes)
      );
      tdsContent.push(excelNumberCell(item.conditions.invoiceVolume) ?? ' - ');
      tdsContent.push(item.billingNumber);

      const productionBatch = item.productionBatch.split(',');
      let productionBatchItem = '';

      productionBatch.forEach((batch: string) => {
        productionBatchItem += batch;
        productionBatchItem += '\r\n';
      });
      tdsContent.push(productionBatchItem);

      trs.push({ tdsContent });
    });

    return trs;
  };
  const allTrs = getTableLines();

  return (
    <tbody>
      <tr style={{ fontSize: '14px', fontWeight: 'bold' }}>
        <th align="center" style={{ background: docTypeHeaderColor }}></th>
        <th
          align="center"
          colSpan={3}
          style={{ background: notesHeaderColor }}
        ></th>
        <th
          align="center"
          colSpan={10}
          style={{ background: headerBackgroundColorBlue }}
        >
          DETALHES DO ITEM FATURADO
        </th>
        <th
          align="center"
          colSpan={6}
          style={{ background: orderDetailHeaderColor }}
        >
          DETALHES DO PEDIDO
        </th>
        <th
          align="center"
          colSpan={5}
          style={{ background: billedDetailHeaderColor }}
        >
          DETALHES DA FATURA
        </th>
      </tr>

      <tr
        style={{
          fontSize: '14px',
          verticalAlign: 'middle',
          whiteSpace: 'pre-wrap'
        }}
      >
        {allTitleTds.map((td) => (
          <td
            key={td.title}
            align="center"
            style={{
              height: `80px`,
              width: `${td.width}px`,
              background: td.backgroundColor
            }}
          >
            {td.title}
          </td>
        ))}
      </tr>

      {allTrs.map((trs, idx) => (
        <tr key={idx}>
          {trs.tdsContent.map((td, tIndex) => {
            const numberCell = isExcelNumberCell(td) ? td : null;
            return (
              <td
                key={`${idx}-${tIndex}`}
                data-excel-number={numberCell?.value}
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  background: idx & 1 ? evenRowColor : 'white'
                }}
              >
                {numberCell ? numberCell.value : td}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

export default OpenOrderTableContentToExcel;
